import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Brain, Bot, Sparkles, Building2, Database, Shield, ArrowRight } from 'lucide-react';

const AIIntelligentSystems = () => {
  const services = [
    {
      icon: Sparkles,
      title: 'Enterprise GenAI Platforms',
      description: 'Private deployments of GPT, Claude, Gemini and LLaMA models for organizations. Secure AI that runs on your data within your infrastructure.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop'
    },
    {
      icon: Bot,
      title: 'AI Copilots for Operations',
      description: 'AI assistants for business operations, finance, HR, IT, compliance, customer support and management teams. Intelligent automation for every department.',
      image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=600&h=400&fit=crop'
    },
    {
      icon: Brain,
      title: 'Autonomous AI Agents (Agentic AI)',
      description: 'Self-operating digital workers built using LangGraph, CrewAI and AutoGen to perform tasks, make decisions and coordinate workflows autonomously.',
      image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&h=400&fit=crop'
    },
    {
      icon: Building2,
      title: 'AI for Enterprises & Government',
      description: 'AI solutions for decision support, analytics, automation, fraud detection and compliance. Enterprise-ready AI systems for critical operations.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop'
    },
    {
      icon: Database,
      title: 'AI for ERP, CRM, Support & Back-Office',
      description: 'AI systems that read business data and automate processes across enterprise platforms. Seamless integration with existing systems.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'
    }
  ];

  const technologies = [
    'GPT-4', 'Claude', 'Gemini', 'LLaMA', 'LangGraph', 
    'CrewAI', 'AutoGen', 'RAG', 'Vector Databases'
  ];

  const benefits = [
    {
      title: 'Private & Secure',
      description: 'AI models deployed within your infrastructure, not public SaaS tools'
    },
    {
      title: 'Enterprise-Ready',
      description: 'Built for compliance, governance and enterprise security standards'
    },
    {
      title: 'Data Sovereignty',
      description: 'Your data stays within your environment, full control maintained'
    },
    {
      title: 'Scalable Architecture',
      description: 'From pilot to production, scale AI across your organization'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=800&fit=crop" 
            alt="AI Systems" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Brain className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-purple-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">AI & Intelligent Systems</h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-4 font-semibold">
              Enterprise-Grade AI, GenAI & Autonomous Agent Platforms
            </p>
            <p className="text-lg md:text-xl text-blue-200 mb-8">
              Sanghvi Infotech designs private, secure, enterprise-grade AI platforms that operate on customer data inside their own environments — not on public SaaS AI tools.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-purple-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Discuss Your AI Strategy
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our AI Solutions</h2>
            <div className="w-24 h-1 bg-purple-600 mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Deploy AI that works on your data, within your security perimeter
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Supported Technologies</h2>
            <div className="w-24 h-1 bg-purple-600 mx-auto mb-6"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <span className="font-semibold text-gray-800">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Private AI</h2>
            <div className="w-24 h-1 bg-purple-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-none shadow-lg text-center p-6">
                <Shield className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-purple-900 to-indigo-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Deploy Enterprise AI?</h2>
          <p className="text-lg md:text-xl mb-8 text-purple-100 max-w-2xl mx-auto">
            Let's build your private, secure AI infrastructure that operates on your data, within your control.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-purple-900 hover:bg-purple-50 text-lg px-8 transition-all hover:scale-105">
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AIIntelligentSystems;